import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MONITOR_STATUSES, ProcessStatus } from '../../../../models/process-status';
import { ProcessStatusFacade } from '../process-status.facade';
import { Observer, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { State, Status } from '../../../../models/status';
import { ProcessService } from '../../../../../services/process.service';
import { MainFacade } from '../../../../main.facade';
import { TreeNode } from 'primeng/api';
import { FileProject, IFileInfo } from '../../../../models/file-info';
import { ProcessName, ProcessType } from '../../../../models/request';

const PROCESS_DISPLAY_NAMES: Record<string, string> = {
  [ProcessType.ALIGNMENT]:     ProcessName.ALIGNMENT,
  [ProcessType.CROP]:          ProcessName.CROP,
  [ProcessType.CONVERT]:       ProcessName.CONVERT,
  [ProcessType.DECONVOLUTION]: ProcessName.DECONVOLUTION,
  [ProcessType.EDOF]:          ProcessName.EDOF,
  [ProcessType.FIND]:          ProcessName.FIND,
  [ProcessType.HTQUANT]:       ProcessName.HTQUANT,
  [ProcessType.KIDNEY]:        ProcessName.KIDNEY,
  [ProcessType.RETINAL_LAYER]: ProcessName.RETINAL_LAYER,
  [ProcessType.STAIN_ADAPTER]: ProcessName.STAIN_ADAPTER,
  [ProcessType.YOLOSEGDETECT]: ProcessName.YOLOSEGDETECT,
  [ProcessType.CELLPOSE]:      ProcessName.CELLPOSE,
  [ProcessType.SCRIPT_QUPATH]: ProcessName.SCRIPT_QUPATH,
  [ProcessType.SCRIPT_IMAGEJ]: ProcessName.SCRIPT_IMAGEJ,
};

/** Matches gs://, s3://, nio:// URIs and captures (bucket, path) */
const STORAGE_URI_RE = /^(?:gs|s3|nio):\/\/([^/]+)\/(.*)/;

@Component({
  selector: 'process-status-item',
  templateUrl: './process-status-item.component.html',
  styleUrls: ['./process-status-item.component.scss']
})
export class ProcessStatusItemComponent implements OnInit, OnDestroy {
  protected readonly State = State;
  monitorStatuses = MONITOR_STATUSES;

  private projects: FileProject[] = [];

  resultsInfo!: string;
  resultsTooltip!: string;
  state!: string;
  percentComplete = 0.0;
  message!: string;
	
	selectedNodeSubscription!: Subscription;
	parentNode!: TreeNode<IFileInfo> | undefined;

	subscription$!: Subscription;
  private readonly destroy$ = new Subject<void>();

	@Input() processStatus!: ProcessStatus;


  get toolDisplayName(): string {
    const info = this.processStatus?.info ?? '';
    return PROCESS_DISPLAY_NAMES[info] ?? info;
  }

  get isActive(): boolean {
    return MONITOR_STATUSES.includes(this.state);
  }

  get hasResults(): boolean {
    return !!(this.processStatus?.results?.length);
  }

  get shortInputPath(): string {
    const raw = this.processStatus?.submissionInput;
    if (!raw) return '';
    return this.formatStoragePath(raw);
  }

  get shortMessage(): string {
    return this.message || '';
  }

  get rowTooltip(): string {
    const uri = this.resultsTooltip || this.processStatus?.submissionInput || '';
    return this.formatStoragePath(uri);
  }

  /**
   * Converts a storage URI (gs://, s3://, nio://) to a human-readable
   * "Project Name / path/inside/bucket" string using the known project list.
   * Falls back to the raw URI if no matching project is found.
   */
  formatStoragePath(uri: string | null): string {
    if (!uri) return '';
    const match = STORAGE_URI_RE.exec(uri);
    if (!match) return uri;
    const [, bucket, path] = match;
    const project = this.projects.find(p => p.rawData?.project?.bucket === bucket);
    const prefix = project ? project.name : bucket;
    return `${prefix}/${path}`;
  }

  constructor(private facade: ProcessStatusFacade, private processService: ProcessService,
              private mainFacade: MainFacade) {}

  ngOnInit(): void {
    this.mainFacade.getSelectedNode$()?.pipe(takeUntil(this.destroy$)).subscribe(parentNode => {
      this.parentNode = parentNode?.parent;
    });
    this.mainFacade.getProjects$().pipe(takeUntil(this.destroy$)).subscribe(projects => {
      this.projects = projects;
    });
    this.parseResults(this.processStatus.results);
    this.state = this.processStatus.state;
    this.percentComplete = this.getPercentComplete(this.processStatus.complete);
    this.message = this.processStatus.message;
    if (MONITOR_STATUSES.includes(this.processStatus.state)) {
      this.startMonitor();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.subscription$) {
      this.subscription$.unsubscribe();
    }
  }

  /**
   * Subscribe to the service monitor for the process
   *
   * @private
   */
  private startMonitor() {
    this.subscription$ = this.processService.monitor(
      this.processService.createMonitorUrl(this.processStatus.workflowId),
      this.createSubmitObserver(this.processStatus.workflowId));
  }

  public createSubmitObserver(workflowId: string) : Observer<Status> {
    return {
      next: (status: Status) => {
        if (status) {
          this.updateStatus(status);
        } else {
          console.error('No status available for observer to process');
        }
      },
      error: error => {
        console.error(`Error in submit observer: ${error}`);
      },
      complete: () => {
        // ok
      }
    }
  }

  private updateStatus(status: Status) {
    if (status) {
      this.parseResults(status.results);
      this.state = (status.state) ? status.state : State.NONE;
      this.percentComplete = this.getPercentComplete(status.complete);
      this.message = (status.message) ? status.message : '';
      this.manageState();
    }
  }

  /**
   * If the state is COMPLETE or ERROR, show an applicable message and unsubscribe from the monitor
   *
   * @private
   */
  private manageState() {
    if (this.state === State.COMPLETE) {
      const summary = 'Process Complete';
      // Message should not say files generated,
      // not all processes generate files.
      const content = `${this.message}.`;
      this.facade.successMessage(summary, content);
      if (this.subscription$) { this.subscription$.unsubscribe(); }

    } else if (this.state == State.ERROR) {
      const summary = `Process Error - ${this.processStatus.info}`;
      const content = this.message.length > 75 ? `Please contact support. ${this.processStatus.workflowId}` : `${this.message}`;
      this.facade.errorMessage(summary, content);
      if (this.subscription$) { this.subscription$.unsubscribe(); }
    }
  }

  /**
   * Extract relevant information from the results field of the Status response object
   *
   * @param results
   * @private
   */
  private parseResults(results: string[] | null | undefined) {
    if (results && results.length > 0) {
      const uri = results[0];
      const match = STORAGE_URI_RE.exec(uri);
      if (match) {
        const [, , path] = match;
        const subdirectory = path.startsWith('/') ? path : `/${path}`;
        this.resultsInfo = subdirectory;
      } else {
        this.resultsInfo = uri;
      }
      this.resultsTooltip = uri;
    } else {
      this.resultsInfo = 'NONE';
      this.resultsTooltip = '';
    }
  }

  public showTerminate(): boolean {
    return this.state === State.RUNNING || this.state === State.SUBMITTED;
  }

  private getPercentComplete(complete: number | undefined) {
    if (complete) {
      return Math.round(complete * 100.0); // status has it as a fraction.
    } else {
      return 0.0;
    }
  }

  public onClickTerminate() {
    this.facade.terminateProcess(this.processStatus.workflowId);
  }

  public onViewResults() {
    const uri = this.processStatus?.results?.[0];
    if (!uri) return;

    if (STORAGE_URI_RE.test(uri)) {
      // openResultPath (not navigateToResults) shows the file immediately
      // via a single fast call and only walks the tree separately to
      // highlight it in the Files tab - see MainFacade.openResultPath. It
      // also tolerates the result being in a different project than
      // whatever is currently selected, which navigateToResults does not.
      this.mainFacade.openResultPath(uri);
    } else {
      // Not every Status.results entry is a storage URI - parseResults()
      // already tolerates a plain relative path (e.g. "output_file.jpg"),
      // and openResultPath's GET /file-from-uri requires gs://s3://nio://
      // and fails on one. Fall back to the tree-relative navigation this
      // used before openResultPath existed: relative to whichever project
      // is currently selected.
      this.mainFacade.navigateToResults(uri);
    }
  }

  public trimBucket(file: string | null) {
    if (file){
      return file.split(/^(gs|nio|s3):\/\/[^/]+\/(.*)/).filter(n => n)[1];
    }
    return '';
  }

  public trimBucketAndFolder(file: string | null) {
    if (file){
			// TODO Make sure all tools return something that the user can use.
      //return this.trimBucket(file).split(/^(.*)\/([^/]*)$/).filter(n => n)[0];
			return file;
    }
    return '';
  }

  public determineIconFromState(): string {
    if (this.state === State.SUBMITTED){
      return 'pi pi-cloud-upload'
    } else if (this.state === State.RUNNING){
      return 'pi pi-spin pi-spinner'
    } else if (this.state === State.COMPLETE) {
      return 'pi pi-check-circle'
    } else if (this.state === State.ERROR) {
      return 'pi pi-exclamation-circle'
    } else if (this.state == State.CANCELLED) {
      return 'pi pi-times-circle'
    } else {
      return 'pi pi-question-circle'
    }
  }

  public tooltipContent() {
    if (this.state === State.ERROR) {
      return `${this.state}\n\n${this.processStatus.workflowId}`
    }
    return this.state;
  }
}
